import { useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { differenceInDays, format, subDays } from 'date-fns';
import { X } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMetricSeries } from '@/hooks/use-analytics-v2';
import type { ScalarMetricKey, SeriesMetricKey } from '@/types/analyticsV2';
import { OverviewSection } from './overview-section';
import { TicketMetricsSection } from './ticket-metrics-section';
import { TicketBreakdownSection } from './ticket-breakdown/ticket-breakdown-section';
import { CoverageFunnelSection } from './coverage-funnel-section';
import { MetricChart } from './metric-chart';
import { ChartMetricSelector } from './chart-metric-selector';
import { GranularitySelector } from './granularity-selector';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { metricDefs, isMetricSeriesAvailable } from '../metric-defs';
import { useChartGranularity } from '../use-chart-granularity';
import { ANALYTICS_MAX_RANGE_DAYS } from '../range-cap';

type DashboardTab = 'message' | 'ticket' | 'funnel';

const DASHBOARD_TABS: { value: DashboardTab; label: string }[] = [
  { value: 'message', label: 'Message Metrics' },
  { value: 'ticket', label: 'Ticket metrics' },
  { value: 'funnel', label: 'Coverage Funnel' },
];

// Label for the previous period the backend compares against (same length, ending the day before).
const buildComparison = (dateRange: DateRange | undefined): { label?: string } => {
  if (!dateRange?.from || !dateRange?.to) return {};
  const lengthDays = differenceInDays(dateRange.to, dateRange.from) + 1;
  const prevTo = subDays(dateRange.from, 1);
  const prevFrom = subDays(prevTo, lengthDays - 1);
  return { label: `${format(prevFrom, 'd MMM, yy')} – ${format(prevTo, 'd MMM, yy')}` };
};

interface SingleAccountAnalyticsProps {
  accountId: number;
  accountName?: string;
  // Same window the list showed, so the overview cards resolve from the seeded cache.
  initialDateRange: DateRange | undefined;
  onClose?: () => void;
}

// Drawer-only (not the public /analytics-v2 dashboard), so the drawer-specific header lives here.
export function SingleAccountAnalytics({ accountId, accountName, initialDateRange, onClose }: SingleAccountAnalyticsProps) {
  const [dateRange, setDateRange] = useState<DateRange | undefined>(initialDateRange);
  const [activeTab, setActiveTab] = useState<DashboardTab>('message');
  const [chartMetric, setChartMetric] = useState<ScalarMetricKey>('aiMessages');

  const fromDate = dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : '';
  const toDate = dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : '';

  const [granularity, setGranularity] = useChartGranularity(fromDate, toDate);
  const comparison = useMemo(() => buildComparison(dateRange), [dateRange]);

  const safeChartMetric: ScalarMetricKey = isMetricSeriesAvailable(chartMetric) ? chartMetric : 'aiMessages';
  const chartDef = metricDefs[safeChartMetric];
  const activeSeriesKey = chartDef.chart?.seriesKey;
  const seriesKeys = useMemo<SeriesMetricKey[]>(
    () => (activeSeriesKey ? [activeSeriesKey] : []),
    [activeSeriesKey],
  );

  const seriesQuery = useMetricSeries(seriesKeys, fromDate, toDate, granularity, accountId);
  const activeSeries = activeSeriesKey ? seriesQuery.data?.[activeSeriesKey] : undefined;

  return (
    <div className='flex flex-col'>
      {/* Sticky so the identity, tabs and date stay reachable while the body scrolls. */}
      <div className='sticky top-0 z-10 bg-background'>
        <div className='flex items-center justify-between gap-3 border-b border-gray-200 px-6 py-4'>
          <div className='min-w-0'>
            <h2 className='truncate text-base font-semibold text-ink'>
              {accountName ?? `Account #${accountId}`}
            </h2>
            <p className='text-xs text-muted-foreground'>Account ID {accountId}</p>
          </div>
          {onClose && (
            <button
              type='button'
              onClick={onClose}
              aria-label='Close panel'
              className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700'
            >
              <X className='h-4 w-4' />
            </button>
          )}
        </div>

        <div className='flex flex-wrap items-center justify-between gap-3 px-6 py-3'>
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as DashboardTab)} variant='segmented'>
            <TabsList>
              {DASHBOARD_TABS.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <DateRangePicker
            inline
            maxDays={ANALYTICS_MAX_RANGE_DAYS}
            value={{ startDate: dateRange?.from, endDate: dateRange?.to }}
            onChange={(range) => setDateRange({ from: range.startDate, to: range.endDate })}
          />
        </div>
      </div>

      <div className='flex flex-col gap-6 px-6 pb-8 pt-4'>
        {activeTab === 'message' && (
          <div className='flex flex-col gap-6'>
            <OverviewSection
              fromDate={fromDate}
              toDate={toDate}
              comparisonLabel={comparison.label}
              overrideAccountId={accountId}
            />
            <div className='flex flex-col gap-3'>
              <h2 className='text-[12px] text-gray-800 font-medium'>Trends</h2>
              <MetricChart
                def={chartDef}
                series={activeSeries}
                isLoading={seriesQuery.isLoading}
                selector={<ChartMetricSelector value={safeChartMetric} onChange={setChartMetric} />}
                controls={<GranularitySelector value={granularity} onChange={setGranularity} />}
              />
            </div>
          </div>
        )}

        {activeTab === 'ticket' && (
          <div className='flex flex-col gap-6'>
            <TicketMetricsSection
              fromDate={fromDate}
              toDate={toDate}
              comparisonLabel={comparison.label}
              overrideAccountId={accountId}
            />
            <TicketBreakdownSection key={accountId} fromDate={fromDate} toDate={toDate} overrideAccountId={accountId} />
          </div>
        )}

        {activeTab === 'funnel' && (
          <CoverageFunnelSection fromDate={fromDate} toDate={toDate} overrideAccountId={accountId} />
        )}
      </div>
    </div>
  );
}
